import { useState, useEffect, useRef, useCallback } from "react";
import { ClipboardList } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import api from "@/lib/api-client";
import { getChangeRequestAppointmentLink, getChangeRequestReservationLink } from "@/lib/notifications";

/**
 * Campanita EXCLUSIVA de solicitudes de cambio de reserva.
 *
 * Convive con la campanita general (NotificationsDropdown): las solicitudes
 * exigen una decisión (aprobar/rechazar) y mezcladas con las notificaciones de
 * nuevas reservas se pasaban por alto. Esta solo cuenta y lista
 * `reservation_change_request`; al pinchar una se marca leída y navega a la
 * reserva, cuyo detalle ya trae el bloque de aprobar/rechazar.
 */

interface ChangeRequestNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  data: Record<string, unknown> | null;
  isRead: boolean;
  createdAt: string;
}

// Las solicitudes de RESERVA y las de CITA se deciden en el mismo sitio: el
// equipo no tiene por qué saber en qué modelo vive cada una (18-sep-2026).
const TYPES = ["reservation_change_request", "appointment_change_request"];
const TYPE = TYPES.join(",");

export default function ChangeRequestsBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<ChangeRequestNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get("/notifications/count", {
        params: { type: TYPE },
      });
      if (response.data.success) {
        // Misma forma de respuesta que lee NotificationsDropdown: {data:{count}}
        setUnreadCount(response.data.data?.count ?? 0);
      }
    } catch {
      // Silencio: un fallo puntual del contador no debe molestar
    }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/notifications", {
        params: { type: TYPE, limit: 10 },
      });
      if (response.data.success) {
        setItems(response.data.data);
      }
    } catch {
      // Silencio
    } finally {
      setLoading(false);
    }
  }, []);

  // Mismo patrón de sondeo que la campanita general: cada 30 s solo con la
  // pestaña visible, con refresco inmediato al volver.
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    const stop = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };
    const start = () => {
      if (interval === null) interval = setInterval(fetchUnreadCount, 30000);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchUnreadCount();
        start();
      } else {
        stop();
      }
    };
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (isOpen) fetchItems();
  }, [isOpen, fetchItems]);

  // Cerrar al pinchar fuera
  useEffect(() => {
    if (!isOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isOpen]);

  const handleItemClick = (item: ChangeRequestNotification) => {
    // OJO: aquí NO se marca como leída a propósito (18-sep-2026). Antes el
    // número bajaba solo con ABRIR la solicitud, así que una que mirabas y
    // dejabas a medias desaparecía de tu radar sin haber decidido nada. Ahora
    // el contador significa "pendientes de decidir": las marca leídas el
    // backend cuando apruebas o rechazas.
    setIsOpen(false);
    const link =
      item.type === "appointment_change_request"
        ? getChangeRequestAppointmentLink(item.data)
        : getChangeRequestReservationLink(item.data);
    if (link) navigate(link);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 text-gray-600"
        title="Solicitudes de cambio de reserva"
      >
        <ClipboardList className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-amber-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        // En móvil el panel va FIJO a los bordes de la pantalla: anclado al
        // botón (absolute right-0) se salía por el lado izquierdo.
        <div className="fixed inset-x-2 top-[calc(64px+var(--safe-top))] sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 w-auto sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[80]">
          <div className="px-4 py-3 border-b border-gray-200 bg-amber-50">
            <p className="text-sm font-semibold text-amber-800">
              Solicitudes de cambio
            </p>
            <p className="text-xs text-amber-700">
              Cambios pedidos por clientes pendientes de decidir
            </p>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {loading ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                Cargando…
              </div>
            ) : items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No hay solicitudes de cambio
              </div>
            ) : (
              items.map((item) => {
                // Ficha estructurada: nombre en negrita + día/hora de la
                // reserva + qué pide cambiar. La frase completa del mensaje se
                // partía en trozos ilegibles en móvil. Las notificaciones
                // antiguas (sin customerName en data) caen al mensaje entero.
                const data = item.data || {};
                const name =
                  typeof data.customerName === "string"
                    ? data.customerName
                    : null;
                const rawDate =
                  typeof data.rawDate === "string"
                    ? data.rawDate
                    : typeof data.currentDate === "string"
                      ? data.currentDate.slice(0, 10)
                      : null;
                const day = rawDate
                  ? `${rawDate.slice(8, 10)}/${rawDate.slice(5, 7)}`
                  : null;
                const time =
                  typeof data.currentTime === "string"
                    ? data.currentTime
                    : null;
                const changes = Array.isArray(data.changes)
                  ? (data.changes as string[]).join(" · ")
                  : null;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                      item.isRead ? "" : "bg-amber-50/60"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        {name ? (
                          <>
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {name}
                            </p>
                            <p className="text-xs text-gray-600 mt-0.5">
                              {day && `📅 ${day}`}
                              {time && ` · 🕐 ${time}`}
                            </p>
                            {changes && (
                              <p className="text-xs text-amber-700 mt-0.5">
                                {changes}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-gray-800 leading-snug">
                            {item.message}
                          </p>
                        )}
                      </div>
                      {!item.isRead && (
                        <span className="mt-1 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDistanceToNow(new Date(item.createdAt), {
                        addSuffix: true,
                        locale: es,
                      })}
                    </p>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
